import * as Sentry from '@sentry/nextjs';
import { FirebaseError } from 'firebase/app';
import SettingConfig from '../config/setting';
import { BaseHttpResponseError } from '../utils/error/apiErrors';
import { ContextualError } from '../utils/error/context-error';

const reg = /[0-9\r\n\s]/g;
const excepts = [
  '비밀번호가 일치하지 않습니다. 다시 입력해 주세요.',
  '방송정보가 없습니다.',
  '종료된 방송입니다.',
  '로그인 후 이용해 주세요.',
  '추천은 하루에 한번만 가능합니다.',
  '조회할 투표가 없습니다.',
  '입장 암호를 확인해 주세요.',
  '종료되거나 확인할 수 없는 방송입니다. 다시 시도해 주세요.',
  '수신설정이 필요합니다.',
  '현재 다른 플레이어로 방송 중입니다.',
  '동시에 최대 1개 파일의 준비가 가능 합니다. 잠시 후 다시 시도 해 주세요.',
  '보낼 내용을 입력해 주세요.',
  '제목을 입력해 주세요',
  '사용 중인 아이디입니다. 다시 입력해 주세요',
  '잘못된 설정입니다.',
  '등록 불가능한 파일입니다.',
  '이미 제재되어있습니다.',
  '본인인증 후 이용해 주세요.',
  'png, jpg, jpeg 확장자만 첨부 가능합니다.',
  '이미 신청한 상담이 있습니다.',
  '강제퇴장된 방송은 입장할 수 없습니다.',
  '선물한 회원이 없습니다.',
  '잘못된 아이디 입니다.',
  '지금은 BJ가 미션요청을 받지 않습니다',
  '쿠폰 번호를 다시 확인해 주세요',
  '제목에 사용할수 없는 문자가 들어있습니다',
  '입력하신 전화번호를 다시 확인해 주세요',
  '인증정보 유효시간이 초과 되었습니다.',
  '관리자에 의해 제재된 회원입니다. 고객센터로 문의해 주세요.',
  '환전 신청 가능 금액은 50,000원 및 후원 500개 이상만 가능합니다.',
  '자신을 북마크 할 수는 없습니다.',
  '잘못된 아이디 입니다.',
  '금칙어 입니다.',
  '환전요청 내역이 없습니다.',
  '가입된 아이디가 없습니다. 다시 확인해 주세요.',
  '유효시간이 초과하였습니다.',
  '계좌번호를 확인해 주세요.',
  '채팅금지 상태입니다.',
  '최대 시도 횟수를 초과했습니다. 24시간 후 시도해 주세요.',
  '검색어를 입력해 주세요.',
  '기존 비밀번호가 일치하지 않습니다.',
  '탈퇴 후 30일 이내에 재가입 하실 수 없습니다',
  '인증코드를 확인해 주세요.',
  '취소할 즐겨찾기가 존재하지 않습니다',
  '변경할 비밀번호가 같지 않습니다.',
  '미션 설정이 off되어 있습니다.',
  '대기중인 미션만 수락 가능합니다.',
  '이미 취소된 미션입니다.',
  '보유한 하트가 부족합니다. 충전 후 이용해 주세요.',
  '24시간 내 5회 이상 미션 취소가 누적되어 24시간 내 미션 요청이 불가합니다.',
  '이미 신고한 쪽지 입니다.',
  '이후 날짜부터 수정이 가능합니다.',
  '인증정보 유효시간이 초과 되었습니다.',
  '닉네임은 2~12자의 한글, 영문, 숫자를 입력해 주세요.',
  '닉네임에 공백을 사용할 수 없습니다.',
  '컨버팅이 이미 완료되었습니다.',
  '충전',
  '자동 접속 방지 체크박스를 확인해 주세요.',
  '보유한 풀방입장권이 없습니다. 아이템을 구매하시겠습니까?',
  '제재된 IP입니다.',
  '본인인증이 필요합니다.',
  '이미 신청중인 내역이 있습니다',
  '다시 입력해 주세요',
  '다시 입력해 주세요',
  'VIP 전용 스페셜 시그니처 이모티콘입니다. 다시 확인 해 주세요',
  '부족합니다',
  '이미지는 3MB 이하로 등록가능합니다',
  '제한하여 발송이 불가능합니다',
  '금일 BJ알림이 1회 발송되었습니다',
  'ID를 확인해 주세요',
  '연속 가입이 불가합니다. 잠시 후 다시 시도해 주세요',
  '해당 IP에서 추천할 수 있는 횟수를 초과하였습니다',
  '이미 즐겨찾기가 되어있습니다',
  '취소할 매니저가 존재하지 않습니다.',
  '본인인증이 필요합니다.',
  '종료되거나 확인할 수 없는 방송입니다. 다시 시도해 주세요.',
  '비밀번호가 일치하지 않습니다. 다시 입력해 주세요.',
  '하트 300개가 부족합니다. 하트를 충전하시겠습니까?',
  "Messaging: This browser doesn't support the API's required to use the Firebase SDK. (messaging/unsupported-browser).",
].map((v) => v.replace(reg, ''));

export async function handleBeforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
  const error = hint.originalException;

  // 중복 억제는 Sentry 기본 dedupeIntegration에 맡긴다. 기존의 세션 전역 Set은 한 번 본 에러를
  // 세션 내내(재발 포함) 통째로 버려서 "beforeSend returned null, will not send"의 원인이 됐다.

  // FirebaseError는 Firebase에서 발생하는 오류로, Sentry에 전송하지 않음
  if (error instanceof FirebaseError) return null;

  // backend 쪽에서 발생하는 오류는  제외
  if (error instanceof BaseHttpResponseError && excepts.includes(error.serverResponse.message.replace(reg, ''))) {
    return null;
  }

  if (error instanceof Error && excepts.includes(error.message.slice(0, 8).replace(reg, ''))) return null;

  try {
    if (error instanceof BaseHttpResponseError) {
      const customContext: (typeof event)['contexts'] = {};

      for (const [key, val] of Object.entries(error)) {
        if (key === 'http-response' && val instanceof Response) {
          /** 개인정보 노출 방지를 위해 주석 처리. (pw, 주소 등)
           const res: Response = val.clone();
           if (!res) continue;
           const serializedHeaders: Record<string, string> = {};
           res.headers.forEach((value, headerkey) => {
             serializedHeaders[headerkey] = value;
           });
           const serializedBody = parseJsonSafely(await res.clone().text());
           const serializableContextValues = {
             status: res.status,
             statusText: res.statusText,
             url: res.url,
             body: serializedBody,
             headers: serializedHeaders,
           };
           customContext[key] = serializableContextValues;
           */
        } else {
          customContext[key] = val;
        }
      }

      event.contexts = {
        ...event.contexts,
        BaseHttpResponseErrorContext: customContext,
      };
    }
  } catch (error) {
    const contextual = new ContextualError('Failed to serialize additional context', {
      cause: error,
    });
    Sentry.captureException(contextual);
  }

  try {
    if (error instanceof ContextualError) {
      const customContext: (typeof event)['contexts'] = {};
      customContext.ContextualError = {
        message: error.message,
        context: error.context,
      };
      event.contexts = {
        ...event.contexts,
        ContextualErrorContext: customContext,
      };
    }
  } catch (error) {
    const contextual = new ContextualError('Failed to serialize additional context', {
      cause: error,
    });
    Sentry.captureException(contextual);
  }
  return event;
}

export const sentryInit: Parameters<typeof Sentry.init>[0] = {
  dsn: SettingConfig.SENTRY_DSN,
  environment: SettingConfig.environment,

  integrations: [
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
  ],

  // 파트너로 진입하여 발생하는 오류는 제외 (노이즈, 분석할 수 없는 오류 등 )
  ignoreTransactions: ['/evt/*'],

  sampleRate: SettingConfig.isFanza ? 1 : 0.01,
  tracesSampleRate: SettingConfig.isFanza ? 1 : 0.001,
  profilesSampleRate: SettingConfig.isFanza ? 1 : 0.001,
  // tracePropagationTargets: [/^https:\/\/api\.pandalive\.co\.kr\/v1/],

  beforeSend: handleBeforeSend,
  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: SettingConfig.isFanza ? 0.1 : 0.01,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: true,
};
